<template>
  <main class="live-quiz-page lobby-page">
    <div class="ambient ambient-one" aria-hidden="true"></div>
    <div class="ambient ambient-two" aria-hidden="true"></div>

    <header class="quiz-header">
      <button type="button" class="header-button" aria-label="Zur Startseite" @click="$router.push('/')">
        <v-icon>mdi-arrow-left</v-icon>
      </button>
      <div class="brand-lockup">
        <img :src="brandLogo" alt="WELL COME FIT" width="1200" height="931" />
        <div><strong>LIVE QUIZ</strong><small>Quiz Arena</small></div>
      </div>
      <button v-if="canManage" type="button" class="header-button manage-button" aria-label="Quiz verwalten" @click="$router.push('/quiz/manage')">
        <v-icon>mdi-tune-variant</v-icon>
      </button>
      <span v-else class="header-placeholder"></span>
    </header>

    <section class="entry-shell">
      <div class="hero-copy">
        <span class="live-pill"><i></i> LIVE &amp; BEREIT</span>
        <h1>Bereit fürs<br /><em>nächste Quiz?</em></h1>
        <p>Gib den sechsstelligen Code ein oder wähle direkt eine offene Quiz-Runde.</p>
      </div>

      <button v-if="joinedSession" type="button" class="resume-card" @click="openSession(joinedSession)">
        <span class="resume-icon"><v-icon>{{ joinedSession.state === "paused" ? "mdi-pause" : "mdi-motion-play-outline" }}</v-icon></span>
        <span class="resume-copy">
          <small><i></i> DEIN LAUFENDES QUIZ</small>
          <strong>{{ joinedSession.quizTitle }}</strong>
          <span>{{ resumeDetail(joinedSession) }}</span>
        </span>
        <span class="resume-action">Fortsetzen <v-icon>mdi-arrow-right</v-icon></span>
      </button>

      <v-card dark flat class="join-card">
        <div class="join-card-heading">
          <span><v-icon>mdi-pound</v-icon></span>
          <div><strong>Quiz-Code</strong><small>Der Code wird auf dem Leiterbildschirm angezeigt.</small></div>
        </div>
        <div class="code-row">
          <input
            v-model="code"
            class="code-input"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            aria-label="Sechsstelliger Quiz-Code"
            placeholder="000 000"
            @input="sanitizeCode"
            @keyup.enter="joinByCode" />
          <v-btn class="join-button" color="#58d388" depressed :loading="joining" :disabled="code.length !== 6" @click="joinByCode">
            Beitreten <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-card>

      <section class="sessions-section">
        <div class="section-heading">
          <div><span>Gerade verfügbar</span><h2>Offene Live-Quiz</h2></div>
          <v-btn icon dark class="refresh-button" :loading="loading" aria-label="Liste aktualisieren" @click="fetchSessions">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>

        <div v-if="loading" class="session-grid">
          <v-skeleton-loader v-for="index in 2" :key="index" dark type="article, actions" class="session-skeleton" />
        </div>
        <div v-else-if="availableSessions.length" class="session-grid">
          <button v-for="session in availableSessions" :key="session._id" type="button" class="session-card" @click="openSession(session)">
            <span class="session-card-icon"><v-icon>mdi-head-question-outline</v-icon></span>
            <span class="session-card-copy">
              <span class="session-state" :class="`state-${session.state}`"><i></i>{{ stateLabel(session.state, session.pauseRequested) }}</span>
              <strong>{{ session.quizTitle }}</strong>
              <small>
                <span><v-icon>mdi-account-group-outline</v-icon>{{ session.participantCount }} dabei</span>
                <span><v-icon>mdi-message-text-fast-outline</v-icon>{{ session.totalQuestions }} Fragen</span>
              </small>
            </span>
            <span class="session-card-code">{{ formatCode(session.joinCode) }}</span>
            <v-icon class="session-arrow">mdi-chevron-right</v-icon>
          </button>
        </div>
        <div v-else class="empty-state">
          <span><v-icon>mdi-radar</v-icon></span>
          <h3>{{ joinedSession ? "Keine weiteren Quiz offen" : "Momentan wartet kein Quiz" }}</h3>
          <p>{{ joinedSession ? "Deine laufende Runde findest du direkt oben." : "Sobald ein Leiter eine Runde eröffnet, erscheint sie direkt hier." }}</p>
        </div>
      </section>
    </section>

    <v-snackbar v-model="showError" color="#3a171b" rounded timeout="4500" bottom>
      <v-icon left color="#ff8a94">mdi-alert-circle-outline</v-icon>{{ errorMessage }}
    </v-snackbar>
  </main>
</template>

<script>
import api from "@/api"
import { brandLogo } from "@/constants/brandAssets"
import userService from "@/services/userService"

export default {
  name: "LiveQuizLobby",
  data() {
    return {
      brandLogo,
      code: "",
      sessions: [],
      loading: true,
      joining: false,
      errorMessage: "",
      showError: false,
    }
  },
  computed: {
    canManage() {
      return userService.hasRight("g_quizManage")
    },
    joinedSession() {
      return this.sessions.find((session) => session.joined) || null
    },
    availableSessions() {
      if (!this.joinedSession) return this.sessions
      return this.sessions.filter((session) => String(session._id) !== String(this.joinedSession._id))
    },
  },
  mounted() {
    this.fetchSessions()
  },
  methods: {
    sanitizeCode() {
      this.code = String(this.code || "").replace(/\D/g, "").slice(0, 6)
    },
    async fetchSessions() {
      this.loading = true
      try {
        this.sessions = (await api.liveQuiz.fetchActiveSessions()) || []
      } catch (error) {
        this.showApiError(error, "Aktive Quiz konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    async joinByCode() {
      if (this.code.length !== 6 || this.joining) return
      this.joining = true
      try {
        const session = await api.liveQuiz.fetchSessionByCode(this.code)
        this.openSession(session)
      } catch (error) {
        this.showApiError(error, "Zu diesem Code wurde kein aktives Quiz gefunden.")
      } finally {
        this.joining = false
      }
    },
    openSession(session) {
      this.$router.push({ name: "liveQuizPlayer", params: { sessionId: session._id }, query: { code: session.joinCode } })
    },
    formatCode(code) {
      const value = String(code || "")
      return value.length === 6 ? `${value.slice(0, 3)} ${value.slice(3)}` : value
    },
    resumeDetail(session) {
      if (session.state === "paused") return "Pausiert · dein Spielstand ist gespeichert"
      if (session.state === "lobby") return "Lobby offen · du bist bereits dabei"
      if (session.pauseRequested) return "Pause nach der aktuellen Frage vorgemerkt"
      const questionNumber = Math.max(1, Number(session.currentQuestionIndex || 0) + 1)
      return `Frage ${questionNumber} von ${session.totalQuestions} · live synchronisiert`
    },
    stateLabel(state, pauseRequested = false) {
      if (pauseRequested) return "Pause folgt"
      return {
        lobby: "Lobby offen",
        countdown: "Startet gerade",
        question: "Läuft",
        reveal: "Auflösung",
        leaderboard: "Rangliste",
        paused: "Pausiert",
      }[state] || "Live"
    },
    showApiError(error, fallback) {
      this.errorMessage = (error && error.response && error.response.data && error.response.data.error) || fallback
      this.showError = true
    },
  },
}
</script>

<style scoped>
.live-quiz-page {
  --green: #58d388;
  --green-strong: #00a94f;
  --surface: rgba(19, 25, 22, 0.92);
  --surface-soft: rgba(255, 255, 255, 0.045);
  --border: rgba(255, 255, 255, 0.11);
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  color: #f4f7f5;
  background: radial-gradient(circle at 50% -20%, rgba(0, 150, 64, 0.2), transparent 42%), #080b0a;
  font-size: 1rem;
}

.ambient {
  position: fixed;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(5rem);
  opacity: 0.18;
}

.ambient-one { top: 18%; left: -10rem; width: 22rem; height: 22rem; background: #00a94f; }
.ambient-two { right: -12rem; bottom: 4%; width: 25rem; height: 25rem; background: #1fd981; opacity: 0.1; }

.quiz-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: min(100%, 72rem);
  min-height: 5rem;
  margin: 0 auto;
  padding: 0.75rem 1.25rem;
}

.header-button,
.header-placeholder {
  width: 3rem;
  height: 3rem;
}

.header-button {
  display: grid;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 1rem;
  color: #eef4f0;
  background: rgba(255, 255, 255, 0.055);
  transition: border-color 180ms ease, background 180ms ease;
}

.header-button:hover { border-color: rgba(88, 211, 136, 0.45); background: rgba(88, 211, 136, 0.1); }
.header-button .v-icon { color: currentColor; }

.brand-lockup { display: flex; align-items: center; gap: 0.75rem; }
.brand-lockup > img { display: block; width: 3.25rem; height: 2.5rem; object-fit: contain; }
.brand-lockup div { display: flex; flex-direction: column; }
.brand-lockup strong { font-size: 0.875rem; letter-spacing: 0.12em; }
.brand-lockup small { color: #8fa098; font-size: 0.75rem; }

.entry-shell { position: relative; z-index: 1; width: min(calc(100% - 2rem), 62rem); margin: 1rem auto 0; padding-bottom: 4rem; }
.hero-copy { max-width: 42rem; margin: 2.5rem auto 2rem; text-align: center; }
.live-pill { display: inline-flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; color: var(--green); font-size: 0.75rem; font-weight: 850; letter-spacing: 0.16em; }
.live-pill i,
.session-state i { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: currentColor; box-shadow: 0 0 0 0.35rem rgba(88, 211, 136, 0.1); animation: livePulse 1.8s ease-in-out infinite; }
.hero-copy h1 { margin: 0; font-size: clamp(2.5rem, 8vw, 5rem); font-weight: 950; line-height: 0.98; letter-spacing: -0.055em; }
.hero-copy h1 em { color: var(--green); font-style: normal; }
.hero-copy p { max-width: 34rem; margin: 1.25rem auto 0; color: #a8b3ad; font-size: 1rem; line-height: 1.65; }

.resume-card { position: relative; display: grid; width: 100%; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 1rem; margin-bottom: 1rem; padding: 1rem; overflow: hidden; border: 1px solid rgba(88, 211, 136, 0.38); border-radius: 1.5rem; color: #f5f8f6; background: radial-gradient(circle at 0 50%, rgba(88, 211, 136, 0.19), transparent 38%), rgba(20, 32, 25, 0.94); box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.25); text-align: left; transition: border-color 180ms ease, background 180ms ease, translate 180ms ease; }
.resume-card::after { position: absolute; top: -4rem; right: 12%; width: 12rem; height: 8rem; border-radius: 50%; background: rgba(88, 211, 136, 0.08); filter: blur(2rem); content: ""; pointer-events: none; }
.resume-card:hover { translate: 0 -0.125rem; border-color: rgba(88, 211, 136, 0.65); background: radial-gradient(circle at 0 50%, rgba(88, 211, 136, 0.24), transparent 42%), rgba(20, 35, 26, 0.98); }
.resume-icon { display: grid; width: 4rem; height: 4rem; place-items: center; border-radius: 1.25rem; color: #07100a; background: var(--green); box-shadow: 0 0 2.5rem rgba(88, 211, 136, 0.2); }
.resume-icon .v-icon { color: currentColor; font-size: 2rem; }
.resume-copy { display: flex; min-width: 0; flex-direction: column; }
.resume-copy > small { display: inline-flex; align-items: center; gap: 0.4rem; color: var(--green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.11em; }
.resume-copy > small i { width: 0.45rem; height: 0.45rem; border-radius: 50%; background: currentColor; box-shadow: 0 0 0 0.3rem rgba(88, 211, 136, 0.1); }
.resume-copy > strong { margin-top: 0.25rem; overflow: hidden; font-size: 1.25rem; text-overflow: ellipsis; white-space: nowrap; }
.resume-copy > span { margin-top: 0.15rem; color: #a8b5ae; font-size: 0.875rem; }
.resume-action { position: relative; z-index: 1; display: inline-flex; min-height: 3rem; align-items: center; gap: 0.35rem; padding: 0 0.875rem; border-radius: 1rem; color: #07100a; background: var(--green); font-size: 0.875rem; font-weight: 900; }
.resume-action .v-icon { color: currentColor; font-size: 1.25rem; }

.join-card { padding: 1.25rem; border: 1px solid rgba(88, 211, 136, 0.24); border-radius: 1.5rem; background: linear-gradient(135deg, rgba(88, 211, 136, 0.075), transparent 52%), var(--surface) !important; box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.3); }
.join-card-heading { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem; }
.join-card-heading > span { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.875rem; background: rgba(88, 211, 136, 0.12); }
.join-card-heading > span .v-icon { color: var(--green); }
.join-card-heading > div { display: flex; min-width: 0; flex-direction: column; }
.join-card-heading strong { font-size: 1rem; }
.join-card-heading small { color: #93a199; font-size: 0.875rem; }
.code-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0.75rem; }
.code-input { min-width: 0; height: 4rem; padding: 0 1.25rem; border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 1rem; outline: none; color: #f6faf7; background: rgba(0, 0, 0, 0.24); font-size: 1.75rem; font-weight: 900; letter-spacing: 0.22em; transition: border-color 180ms ease, box-shadow 180ms ease; }
.code-input:focus { border-color: var(--green); box-shadow: 0 0 0 0.25rem rgba(88, 211, 136, 0.09); }
.code-input::placeholder { color: #526059; }
.join-button { min-width: 10rem !important; height: 4rem !important; border-radius: 1rem !important; color: #07100a !important; font-size: 1rem !important; font-weight: 900 !important; text-transform: none !important; }

.sessions-section { margin-top: 2.5rem; }
.section-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.section-heading span { color: var(--green); font-size: 0.75rem; font-weight: 850; letter-spacing: 0.12em; text-transform: uppercase; }
.section-heading h2 { margin: 0.25rem 0 0; font-size: 1.5rem; letter-spacing: -0.025em; }
.refresh-button { border: 1px solid var(--border); background: var(--surface-soft); }
.session-grid { display: grid; gap: 0.75rem; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.session-skeleton { min-height: 10rem; border-radius: 1.25rem; overflow: hidden; }
.session-card { position: relative; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.875rem; min-height: 9rem; padding: 1.125rem; overflow: hidden; border: 1px solid var(--border); border-radius: 1.25rem; color: inherit; background: var(--surface-soft); text-align: left; transition: border-color 180ms ease, background 180ms ease, translate 180ms ease; }
.session-card:hover { translate: 0 -0.125rem; border-color: rgba(88, 211, 136, 0.34); background: rgba(88, 211, 136, 0.07); }
.session-card-icon { display: grid; width: 3.25rem; height: 3.25rem; place-items: center; border-radius: 1rem; background: rgba(88, 211, 136, 0.11); }
.session-card-icon .v-icon { color: var(--green); }
.session-card-copy { display: flex; min-width: 0; flex-direction: column; }
.session-state { display: inline-flex; align-items: center; gap: 0.45rem; width: fit-content; margin-bottom: 0.4rem; color: var(--green); font-size: 0.75rem; font-weight: 800; }
.session-state i { width: 0.4rem; height: 0.4rem; box-shadow: none; }
.state-question { color: #ffd166; }
.state-paused { color: #f7c84b; }
.session-card-copy strong { overflow: hidden; font-size: 1.125rem; text-overflow: ellipsis; white-space: nowrap; }
.session-card-copy small { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.5rem; color: #91a098; font-size: 0.75rem; }
.session-card-copy small span { display: inline-flex; align-items: center; gap: 0.25rem; }
.session-card-copy small .v-icon { color: currentColor; font-size: 0.875rem; }
.session-card-code { align-self: start; padding: 0.35rem 0.55rem; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 0.625rem; color: #c7d0cb; background: rgba(0, 0, 0, 0.2); font-size: 0.75rem; font-weight: 850; letter-spacing: 0.08em; }
.session-arrow { position: absolute; right: 0.75rem; bottom: 0.75rem; color: #617069 !important; }

.empty-state { display: flex; min-height: 13rem; align-items: center; justify-content: center; flex-direction: column; padding: 2rem; border: 1px dashed rgba(255, 255, 255, 0.14); border-radius: 1.25rem; text-align: center; }
.empty-state > span { display: grid; width: 3.5rem; height: 3.5rem; place-items: center; margin-bottom: 0.75rem; border-radius: 1.125rem; background: rgba(255, 255, 255, 0.055); }
.empty-state > span .v-icon { color: #77867e; }
.empty-state h3 { margin: 0; font-size: 1.125rem; }
.empty-state p { margin: 0.5rem 0 0; color: #8e9c95; font-size: 0.875rem; }

@keyframes livePulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }

@media (max-width: 47.9375rem) {
  .quiz-header { min-height: 4.5rem; padding-inline: 1rem; }
  .brand-lockup { gap: 0.5rem; }
  .brand-lockup > img { width: 2.75rem; height: 2.125rem; }
  .entry-shell { width: min(calc(100% - 1.5rem), 38rem); margin-top: 0; }
  .hero-copy { margin-top: 1.5rem; }
  .hero-copy h1 { font-size: clamp(2.5rem, 13vw, 4rem); }
  .resume-card { grid-template-columns: auto minmax(0, 1fr); gap: 0.75rem; padding: 0.875rem; border-radius: 1.25rem; }
  .resume-icon { width: 3.25rem; height: 3.25rem; border-radius: 1rem; }
  .resume-icon .v-icon { font-size: 1.5rem; }
  .resume-copy > strong { font-size: 1rem; }
  .resume-action { grid-column: 1 / -1; justify-content: center; }
  .join-card { padding: 1rem; border-radius: 1.25rem; }
  .code-row { grid-template-columns: 1fr; }
  .code-input { text-align: center; }
  .join-button { width: 100%; }
  .session-grid { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .live-pill i,
  .session-state i { animation: none; }
  .resume-card,
  .session-card { transition: none; }
}
</style>
